<div class="approver-page-container">
  <!-- ── PAGE HEADER ROW: dept pill + KPI stat cards in one 72px row ── -->
  <div class="ap-page-header-row">
    <div class="ap-dept-pill" [attr.title]="approverBlockDisplay().title || null">
      <i class="fa-solid fa-location-dot"></i>
      <div class="ap-dept-pill-text">
        <span class="ap-pill-block">{{ approverBlockDisplay().primary }}</span>
        <span class="ap-pill-dept">{{ (approverDeptDisplay() || 'No Department') | uppercase }}</span>
      </div>
    </div>
    <div class="ap-stat-row">
      <button type="button"
              class="ap-stat-card ap-pending"
              (click)="selectStat('pending')"
              title="Open list of pending approval requests">
        <i class="fa-solid fa-hourglass-half ap-stat-icon"></i>
        <div class="ap-stat-body">
          <span class="ap-stat-label">Pending</span>
          <span class="ap-stat-value">{{ pendingProjectsCount() }}</span>
        </div>
      </button>
      <button type="button"
              class="ap-stat-card ap-approved"
              (click)="selectStat('approved')"
              title="Open approved history">
        <i class="fa-solid fa-circle-check ap-stat-icon"></i>
        <div class="ap-stat-body">
          <span class="ap-stat-label">Approved</span>
          <span class="ap-stat-value">{{ approvedProjectsCount() }}</span>
        </div>
      </button>
      <button type="button"
              class="ap-stat-card ap-rejected"
              (click)="selectStat('rejected')"
              title="Open rejected history">
        <i class="fa-solid fa-circle-xmark ap-stat-icon"></i>
        <div class="ap-stat-body">
          <span class="ap-stat-label">Rejected</span>
          <span class="ap-stat-value">{{ rejectedProjectsCount() }}</span>
        </div>
      </button>
    </div>
  </div>

  <!-- ── PENDING SCHEME REQUESTS (scheme-only submissions, no project attached) ── -->
  @if (pendingSchemeRequestsCount() > 0) {
    <div class="ap-table-section">
      <div class="ap-section-title">
        <i class="fa-solid fa-layer-group"></i>
        <span>Pending Scheme Requests ({{ pendingSchemeRequestsCount() }})</span>
      </div>
      <div class="ap-projects-wrap">
        <table class="ap-projects-table">
          <thead>
            <tr>
              <th class="ap-sl">#</th>
              <th class="ap-name">Scheme Name</th>
              <th>Department</th>
              <th class="ap-num">Budget (₹L)</th>
              <th class="ap-num">Spent (₹L)</th>
              <th class="ap-hide-mobile">Submitted By</th>
              <th class="ap-hide-mobile">Submitted On</th>
              <th class="ap-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (s of pendingSchemeRequests(); track s.id; let i = $index) {
              <tr>
                <td class="ap-num-cell">{{ i + 1 }}</td>
                <td class="ap-name"><span class="ap-name-plain">{{ s.name }}</span></td>
                <td>{{ s.dept }}</td>
                <td class="ap-num">{{ s.totalCost | number:'1.0-2' }}</td>
                <td class="ap-num">{{ s.spent | number:'1.0-2' }}</td>
                <td class="ap-hide-mobile">{{ s.operator }}</td>
                <td class="ap-hide-mobile">{{ lastUpdatedOf({ updatedAt: s.submittedOn }) }}</td>
                <td class="ap-actions-col">
                  <button class="ap-act ap-act-approve"
                          (click)="approveRow(s)"
                          [disabled]="isRowBusy(s.pendingRequest.id)"
                          [title]="isRowBusy(s.pendingRequest.id) ? 'Processing…' : 'Approve this scheme'">
                    @if (isRowBusy(s.pendingRequest.id)) {
                      <i class="fa-solid fa-spinner fa-spin"></i>
                    } @else {
                      <i class="fa-solid fa-check"></i>
                    }
                  </button>
                  <button class="ap-act ap-act-reject"
                          (click)="rejectRow(s)"
                          [disabled]="isRowBusy(s.pendingRequest.id)"
                          [title]="isRowBusy(s.pendingRequest.id) ? 'Processing…' : 'Reject this scheme'">
                    @if (isRowBusy(s.pendingRequest.id)) {
                      <i class="fa-solid fa-spinner fa-spin"></i>
                    } @else {
                      <i class="fa-solid fa-xmark"></i>
                    }
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }

  <!-- ── SINGLE-SCREEN PROJECT TABLE ── -->
  <div class="ap-table-section">
    <div class="ap-section-title">
      <i class="fa-solid fa-list-check"></i>
      <span>Department Projects</span>
    </div>

    <!-- Toolbar: smart search + status filter + reset -->
    <div class="ap-toolbar">
      <div class="ap-toolbar-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input type="text"
               [ngModel]="projectSearch()"
               (ngModelChange)="projectSearch.set($event); opPage.set(1)"
               placeholder="Search…" />
      </div>
      <div class="ap-toolbar-filters">
        <select class="ap-toolbar-select ap-filt-status"
                [ngModel]="projectStatusFilter()"
                (ngModelChange)="projectStatusFilter.set($event); opPage.set(1)"
                aria-label="Filter by status">
          <option value="">Status</option>
          <option value="Planned">Planned</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Stuck">Stuck</option>
        </select>
        <select class="ap-toolbar-select ap-filt-scheme"
                [ngModel]="projectSchemeFilter()"
                (ngModelChange)="projectSchemeFilter.set($event); opPage.set(1)"
                aria-label="Filter by scheme">
          <option value="">Scheme</option>
          @for (s of availableFilterSchemes(); track s) {
            <option [value]="s">{{ s }}</option>
          }
        </select>
        <select class="ap-toolbar-select ap-filt-block"
                [class.ap-filt-span]="availableFilterGPs().length === 0"
                [ngModel]="projectBlockFilter()"
                (ngModelChange)="projectBlockFilter.set($event); projectGpFilter.set(''); opPage.set(1)"
                aria-label="Filter by block">
          <option value="">Block</option>
          @for (b of availableFilterBlocks(); track b) {
            <option [value]="b">{{ b }}</option>
          }
        </select>
        @if (availableFilterGPs().length > 0) {
          <select class="ap-toolbar-select ap-filt-gp"
                  [ngModel]="projectGpFilter()"
                  (ngModelChange)="projectGpFilter.set($event); opPage.set(1)"
                  aria-label="Filter by GP">
            <option value="">GP</option>
            @for (g of availableFilterGPs(); track g) {
              <option [value]="g">{{ g }}</option>
            }
          </select>
        }
        <button class="ap-toolbar-btn ap-filt-clear" (click)="opResetTableFilters()" title="Clear filters" aria-label="Clear filters">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <span class="ap-toolbar-count">{{ filteredProjectRows().length }} of {{ tableBaseRowsCount() }} projects</span>
    </div>

    <!-- Bulk action bar — visible only when ≥ 1 row is selected -->
    @if (selectedCount() > 0) {
      <div class="ap-bulk-bar">
        <div class="ap-bulk-info">
          <i class="fa-solid fa-square-check"></i>
          <span>{{ selectedCount() }} request{{ selectedCount() === 1 ? '' : 's' }} selected</span>
        </div>
        <div class="ap-bulk-actions">
          <button class="ap-btn-secondary"
                  (click)="clearSelection()"
                  [disabled]="bulkActionInProgress()">
            Clear
          </button>
          <button class="ap-btn-approve"
                  (click)="bulkApprove()"
                  [disabled]="bulkActionInProgress()">
            @if (bulkActionInProgress()) {
              <i class="fa-solid fa-spinner fa-spin"></i> Approving…
            } @else {
              <i class="fa-solid fa-check"></i> Approve Selected
            }
          </button>
          <button class="ap-btn-reject"
                  (click)="bulkReject()"
                  [disabled]="bulkActionInProgress()">
            @if (bulkActionInProgress()) {
              <i class="fa-solid fa-spinner fa-spin"></i> Rejecting…
            } @else {
              <i class="fa-solid fa-xmark"></i> Reject Selected
            }
          </button>
        </div>
      </div>
    }

    <!-- Project table (sticky header, horizontally scrollable) -->
    <div class="ap-projects-wrap">
      <p class="ap-scroll-hint ap-hide-desktop" aria-hidden="true">Swipe table for more columns →</p>
      <table class="ap-projects-table">
        <thead>
          <tr>
            <th class="ap-check-col">
              <input type="checkbox"
                     class="ap-checkbox"
                     [checked]="isAllPendingSelected()"
                     [indeterminate]="isSomePendingSelected()"
                     (change)="toggleSelectAllPending()"
                     [disabled]="visiblePendingCount === 0"
                     title="Select all pending requests on this page" />
            </th>
            <th class="ap-sl">#</th>
            <th class="ap-name">Project Name</th>
            <th>Scheme</th>
            <th>GP Name</th>
            <th class="ap-num ap-hide-mobile">Length (km)</th>
            <th class="ap-num">Cost (₹L)</th>
            <th>Status</th>
            <th class="ap-hide-mobile">Last Updated</th>
            <th class="ap-actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (p of opPagedProjects; track p.id; let i = $index) {
            <tr [class.row-phantom]="p.isPhantom"
                [class.row-pending]="p.approvalStatus === 'PENDING'"
                [class.row-selected]="isRowSelected(p.pendingRequest?.id)">
              <td class="ap-check-col">
                @if (p.pendingRequest?.id) {
                  <input type="checkbox"
                         class="ap-checkbox"
                         [checked]="isRowSelected(p.pendingRequest.id)"
                         (change)="toggleRowSelection(p.pendingRequest.id)"
                         [disabled]="isRowBusy(p.pendingRequest.id)"
                         title="Select for bulk action" />
                }
              </td>
              <td class="ap-sl">{{ opSlForIndex(i) }}</td>
              <td class="ap-name">
                <button type="button" class="ap-name-link"
                        (click)="viewProjectRow(p)"
                        [title]="'View project details'">
                  {{ p.name }}
                </button>
                @if (p.recurringIntervention) {
                  <span class="ap-mini-tag ap-tag-rec" title="Recurring Intervention"><i class="fa-solid fa-rotate"></i> REC</span>
                }
              </td>
              <td>{{ p.scheme || '—' }}</td>
              <td>{{ p.gp || '—' }}</td>
              <td class="ap-num ap-hide-mobile">{{ p.lengthKm != null ? (p.lengthKm | number:'1.1-2') : '—' }}</td>
              <td class="ap-num">{{ p.costLakh | number:'1.0-2' }}</td>
              <td><span [class]="projectStatusClass(p.status)">{{ p.status }}</span></td>
              <td class="ap-hide-mobile">{{ lastUpdatedOf(p) }}</td>
              <td class="ap-actions-col">
                <button class="ap-act ap-act-edit"
                        (click)="openEditProjectRow(p)"
                        [disabled]="!isRowEditable(p)"
                        title="Edit project">
                  <i class="fa-solid fa-pen-to-square"></i>
                </button>
                @if (p.pendingRequest) {
                  <button class="ap-act ap-act-approve"
                          (click)="approveRow(p)"
                          [disabled]="isRowBusy(p.pendingRequest.id)"
                          [title]="isRowBusy(p.pendingRequest.id) ? 'Processing…' : 'Approve this request'">
                    @if (isRowBusy(p.pendingRequest.id)) {
                      <i class="fa-solid fa-spinner fa-spin"></i>
                    } @else {
                      <i class="fa-solid fa-check"></i>
                    }
                  </button>
                  <button class="ap-act ap-act-reject"
                          (click)="rejectRow(p)"
                          [disabled]="isRowBusy(p.pendingRequest.id)"
                          [title]="isRowBusy(p.pendingRequest.id) ? 'Processing…' : 'Reject this request'">
                    @if (isRowBusy(p.pendingRequest.id)) {
                      <i class="fa-solid fa-spinner fa-spin"></i>
                    } @else {
                      <i class="fa-solid fa-xmark"></i>
                    }
                  </button>
                }
              </td>
            </tr>
          }
          @if (filteredProjectRows().length === 0) {
            <tr>
              <td colspan="10" class="ap-empty-row">
                <i class="fa-regular fa-folder-open"></i>
                <p>No projects match the current filters.</p>
                @if (projectSearch() || projectStatusFilter() || projectSchemeFilter() || projectBlockFilter() || projectGpFilter()) {
                  <button class="ap-toolbar-btn" (click)="opResetTableFilters()">Clear filters</button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination footer -->
    @if (filteredProjectRows().length > 0) {
      <div class="ap-pagination">
        <div class="ap-pagination-left">
          <span class="ap-pagination-range">{{ opPageRange }}</span>
          <label class="ap-page-size">
            <span>Rows:</span>
            <select [ngModel]="opPageSize()" (ngModelChange)="opSetPageSize($event)">
              <option [ngValue]="10">10</option>
              <option [ngValue]="25">25</option>
              <option [ngValue]="50">50</option>
              <option [ngValue]="100">100</option>
            </select>
          </label>
        </div>
        <div class="ap-pagination-right">
          <button class="ap-page-btn" (click)="opGoToPage(1)" [disabled]="opPage() === 1" title="First page">
            <i class="fa-solid fa-angles-left"></i>
          </button>
          <button class="ap-page-btn" (click)="opPrevPage()" [disabled]="opPage() === 1" title="Previous page">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="ap-page-indicator">Page {{ opPage() }} / {{ opTotalPages }}</span>
          <button class="ap-page-btn" (click)="opNextPage()" [disabled]="opPage() === opTotalPages" title="Next page">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button class="ap-page-btn" (click)="opGoToPage(opTotalPages)" [disabled]="opPage() === opTotalPages" title="Last page">
            <i class="fa-solid fa-angles-right"></i>
          </button>
        </div>
      </div>
    }
  </div>

  <!-- ════════════════════════════════════════════════════════════════
       STAT-CARD POPUP — opens for Pending / Approved / Rejected
       ════════════════════════════════════════════════════════════════ -->
  @if (statModalFilter()) {
    <div class="ap-stat-modal-overlay" (click)="closeStatModal()">
      <div class="ap-stat-modal-card" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <div class="ap-stat-modal-header"
             [class.is-pending]="statModalFilter() === 'pending'"
             [class.is-approved]="statModalFilter() === 'approved'"
             [class.is-rejected]="statModalFilter() === 'rejected'">
          <div class="ap-stat-modal-title-wrap">
            <div class="ap-stat-modal-icon">
              @switch (statModalFilter()) {
                @case ('pending')  { <i class="fa-solid fa-hourglass-half"></i> }
                @case ('approved') { <i class="fa-solid fa-circle-check"></i> }
                @case ('rejected') { <i class="fa-solid fa-circle-xmark"></i> }
              }
            </div>
            <div>
              <h3>{{ statModalTitle() }}</h3>
              <p>{{ statModalRows().length }} entr{{ statModalRows().length === 1 ? 'y' : 'ies' }} · Live from Firestore</p>
            </div>
          </div>
          <button class="ap-stat-modal-close" (click)="closeStatModal()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="ap-stat-modal-body">
          @if (statModalRows().length === 0) {
            <div class="ap-stat-modal-empty">
              <i class="fa-regular fa-folder-open"></i>
              <p>No entries to show.</p>
            </div>
          } @else {
            <!-- Bulk action bar inside the Pending popup -->
            @if (statModalFilter() === 'pending' && selectedCount() > 0) {
              <div class="ap-bulk-bar ap-bulk-bar--inside-modal">
                <div class="ap-bulk-info">
                  <i class="fa-solid fa-square-check"></i>
                  <span>{{ selectedCount() }} selected</span>
                </div>
                <div class="ap-bulk-actions">
                  <button class="ap-btn-secondary"
                          (click)="clearSelection()"
                          [disabled]="bulkActionInProgress()">
                    Clear
                  </button>
                  <button class="ap-btn-approve"
                          (click)="bulkApprove()"
                          [disabled]="bulkActionInProgress()">
                    @if (bulkActionInProgress()) {
                      <i class="fa-solid fa-spinner fa-spin"></i> Approving…
                    } @else {
                      <i class="fa-solid fa-check"></i> Approve Selected
                    }
                  </button>
                  <button class="ap-btn-reject"
                          (click)="bulkReject()"
                          [disabled]="bulkActionInProgress()">
                    @if (bulkActionInProgress()) {
                      <i class="fa-solid fa-spinner fa-spin"></i> Rejecting…
                    } @else {
                      <i class="fa-solid fa-xmark"></i> Reject Selected
                    }
                  </button>
                </div>
              </div>
            }

            <table class="ap-stat-modal-table">
              <thead>
                <tr>
                  @if (statModalFilter() === 'pending') {
                    <th class="ap-check-col">
                      <input type="checkbox"
                             class="ap-checkbox"
                             [checked]="isAllPendingSelected()"
                             [indeterminate]="isSomePendingSelected()"
                             (change)="toggleSelectAllPending()"
                             [disabled]="visiblePendingCount === 0"
                             title="Select all pending requests" />
                    </th>
                  }
                  <th>#</th>
                  <th>Project Name</th>
                  <th>Scheme</th>
                  <th>GP Name</th>
                  <th class="ap-num">Cost (₹L)</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th class="ap-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (p of statModalRows(); track p.id; let i = $index) {
                  <tr [class.row-phantom]="p.isPhantom"
                      [class.row-selected]="isRowSelected(p.pendingRequest?.id)">
                    @if (statModalFilter() === 'pending') {
                      <td class="ap-check-col">
                        @if (p.pendingRequest?.id) {
                          <input type="checkbox"
                                 class="ap-checkbox"
                                 [checked]="isRowSelected(p.pendingRequest.id)"
                                 (change)="toggleRowSelection(p.pendingRequest.id)"
                                 [disabled]="isRowBusy(p.pendingRequest.id)"
                                 title="Select for bulk action" />
                        }
                      </td>
                    }
                    <td class="ap-num-cell">{{ i + 1 }}</td>
                    <td>
                      @if (statModalFilter() === 'pending') {
                        <button type="button" class="ap-name-link"
                                (click)="viewProjectRow(p); closeStatModal()">
                          {{ p.name }}
                        </button>
                      } @else {
                        <span class="ap-name-plain">{{ p.name }}</span>
                      }
                      @if (p.recurringIntervention) {
                        <span class="ap-mini-tag ap-tag-rec"><i class="fa-solid fa-rotate"></i> REC</span>
                      }
                    </td>
                    <td>{{ p.scheme || '—' }}</td>
                    <td>{{ p.gp || '—' }}</td>
                    <td class="ap-num">{{ p.costLakh | number:'1.0-2' }}</td>
                    <td><span [class]="projectStatusClass(p.status)">{{ p.status }}</span></td>
                    <td>{{ lastUpdatedOf(p) }}</td>
                    <td class="ap-actions-col">
                      <button class="ap-act ap-act-edit"
                              (click)="openEditProjectRow(p); closeStatModal()"
                              [disabled]="!isRowEditable(p)"
                              title="Edit project">
                        <i class="fa-solid fa-pen-to-square"></i>
                      </button>
                      @if (statModalFilter() === 'pending' && p.pendingRequest) {
                        <button class="ap-act ap-act-approve"
                                (click)="approveRow(p)"
                                [disabled]="isRowBusy(p.pendingRequest.id)"
                                [title]="isRowBusy(p.pendingRequest.id) ? 'Processing…' : 'Approve'">
                          @if (isRowBusy(p.pendingRequest.id)) {
                            <i class="fa-solid fa-spinner fa-spin"></i>
                          } @else {
                            <i class="fa-solid fa-check"></i>
                          }
                        </button>
                        <button class="ap-act ap-act-reject"
                                (click)="rejectRow(p)"
                                [disabled]="isRowBusy(p.pendingRequest.id)"
                                [title]="isRowBusy(p.pendingRequest.id) ? 'Processing…' : 'Reject'">
                          @if (isRowBusy(p.pendingRequest.id)) {
                            <i class="fa-solid fa-spinner fa-spin"></i>
                          } @else {
                            <i class="fa-solid fa-xmark"></i>
                          }
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>

        <div class="ap-stat-modal-footer">
          <button class="ap-btn-secondary" (click)="closeStatModal()">Close</button>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════════════
       EDIT PROJECT MODAL  (approver submits own change for self-review)
       ════════════════════════════════════════════════════════════════ -->
  @if (isEditModalOpen()) {
    <div class="ap-edit-overlay" (click)="closeEditModal()">
      <div class="ap-edit-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <div class="ap-edit-modal-header">
          <div class="ap-edit-modal-title">
            <i class="fa-solid" [class.fa-pen-to-square]="!pendingEditMode()" [class.fa-pen-line]="pendingEditMode()"
               [style.background]="pendingEditMode() ? '#ecfdf5' : ''" [style.color]="pendingEditMode() ? '#047857' : ''"></i>
            <div>
              @if (pendingEditMode()) {
                <h3>Edit Before Approval</h3>
                <p>Modify the pending data, then approve with your changes.</p>
              } @else {
                <h3>Edit Project</h3>
                <p>Changes will be queued for your review before going live.</p>
              }
            </div>
          </div>
          <button class="ap-stat-modal-close" (click)="closeEditModal()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="ap-edit-modal-body custom-scroll">

          <!-- Department (read-only) -->
          <div class="ap-edit-form-group">
            <label>Department</label>
            <div class="ap-edit-readonly">
              <i class="fa-solid fa-building-columns"></i>
              <span>{{ editFormData().deptName || 'Unknown' }}</span>
            </div>
          </div>

          <!-- Scheme -->
          <div class="ap-edit-form-group">
            <label>Scheme</label>
            <select [ngModel]="editFormData().schemeName"
                    (ngModelChange)="patchEditForm({schemeName: $event})">
              <option value="">-- Select Scheme --</option>
              @for (s of editDeptSchemes(); track s) {
                <option [value]="s">{{ s }}</option>
              }
            </select>
          </div>

          <!-- Project Name -->
          <div class="ap-edit-form-group">
            <label>Project Name <span class="ap-edit-required">*</span></label>
            <input type="text"
                   [ngModel]="editFormData().projectName"
                   (ngModelChange)="patchEditForm({projectName: $event})"
                   placeholder="Enter project name" />
          </div>

          <!-- Block + GP -->
          <div class="ap-edit-form-row">
            <div class="ap-edit-form-group">
              <label>Block <span class="ap-edit-required">*</span></label>
              <select [ngModel]="editFormData().block"
                      (ngModelChange)="onEditBlockChange($event)">
                <option value="">-- Select Block --</option>
                @for (b of locationBlocks(); track b) {
                  <option [value]="b">{{ b }}</option>
                }
              </select>
            </div>
            <div class="ap-edit-form-group">
              <label>Gram Panchayat</label>
              @if (editIsNacBlock()) {
                <input type="text" value="NAC" class="ap-edit-readonly-input" readonly />
              } @else {
                <select [ngModel]="editFormData().gp"
                        (ngModelChange)="patchEditForm({gp: $event})">
                  <option value="">-- Select GP --</option>
                  @for (gp of editGpOptions(); track gp) {
                    <option [value]="gp">{{ gp }}</option>
                  }
                </select>
              }
            </div>
          </div>

          <!-- Status -->
          <div class="ap-edit-form-group">
            <label>Status <span class="ap-edit-required">*</span></label>
            <select [ngModel]="editFormData().status"
                    (ngModelChange)="patchEditForm({status: $event})">
              <option value="Planned">Planned</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Stuck">Stuck</option>
            </select>
          </div>

          <!-- Cost / Spent -->
          <div class="ap-edit-form-row">
            <div class="ap-edit-form-group">
              <label>Total Cost (₹ Lakh) <span class="ap-edit-required">*</span></label>
              <input type="number"
                     [ngModel]="editFormData().costCr"
                     (ngModelChange)="patchEditForm({costCr: $event})"
                     placeholder="0.00" min="0" />
            </div>
            <div class="ap-edit-form-group">
              <label>Amount Spent (₹ Lakh)</label>
              <input type="number"
                     [ngModel]="editFormData().spentCr"
                     (ngModelChange)="patchEditForm({spentCr: $event})"
                     placeholder="0.00" min="0" />
            </div>
          </div>

          <!-- Year / Road Length -->
          <div class="ap-edit-form-row">
            <div class="ap-edit-form-group">
              <label>Year of Creation</label>
              <select [ngModel]="editFormData().yearOfCreation"
                      (ngModelChange)="patchEditForm({yearOfCreation: $event})">
                <option value="">-- Select Year --</option>
                @for (y of editYearOptions(); track y) {
                  <option [value]="y">{{ y }}</option>
                }
              </select>
            </div>
            <div class="ap-edit-form-group">
              <label>Road Length (km)</label>
              <input type="number"
                     [ngModel]="editFormData().roadLength"
                     (ngModelChange)="patchEditForm({roadLength: $event})"
                     placeholder="e.g. 2.5" min="0" step="0.01" />
            </div>
          </div>

          <!-- Executant -->
          <div class="ap-edit-form-row">
            <div class="ap-edit-form-group">
              <label>Executant Name</label>
              <input type="text"
                     [ngModel]="editFormData().executantName"
                     (ngModelChange)="patchEditForm({executantName: $event})"
                     placeholder="Contractor / agency name" />
            </div>
            <div class="ap-edit-form-group">
              <label>Executant Contact</label>
              <input type="tel"
                     [ngModel]="editFormData().executantContact"
                     (ngModelChange)="patchEditForm({executantContact: $event})"
                     placeholder="Mobile number" />
            </div>
          </div>

          <!-- ── Site Photos ── -->
          <div class="ap-edit-section-title">
            <i class="fa-solid fa-images"></i> Site Photos
          </div>

          @if (isEditUploadingPhotos()) {
            <div class="ap-edit-upload-progress-bar">
              <div class="ap-edit-upload-progress-fill" [style.width.%]="editPhotoProgress()"></div>
            </div>
          }

          <div class="ap-edit-photos-grid">

            <!-- Before Work -->
            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-before">
                <i class="fa-solid fa-hourglass-start"></i> Before Work
              </div>
              <div class="ap-edit-photo-date">
                <label>Before Date</label>
                <input type="date" [ngModel]="editFormData().photoDateBefore"
                       (ngModelChange)="patchEditForm({photoDateBefore: $event})" [ngModelOptions]="{standalone: true}">
              </div>
              <div class="ap-edit-photo-thumbs">
                @for (src of editBeforePreviews(); track src; let i = $index) {
                  <div class="ap-edit-thumb">
                    <img [src]="src" alt="Before" />
                    <button type="button" class="ap-edit-thumb-remove"
                            (click)="removeEditPhoto('before', i)" aria-label="Remove">
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                }
                @if (editBeforePreviews().length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
              </div>
              <label class="ap-edit-photo-add">
                <i class="fa-solid fa-plus"></i> Add Photo
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple
                       class="sr-only" (change)="onEditPhotoSelected('before', $event)" />
              </label>
            </div>

            <!-- During Work -->
            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-during">
                <i class="fa-solid fa-person-digging"></i> During Work
              </div>
              <div class="ap-edit-photo-date">
                <label>During Date</label>
                <input type="date" [ngModel]="editFormData().photoDateDuring"
                       (ngModelChange)="patchEditForm({photoDateDuring: $event})" [ngModelOptions]="{standalone: true}">
              </div>
              <div class="ap-edit-photo-thumbs">
                @for (src of editDuringPreviews(); track src; let i = $index) {
                  <div class="ap-edit-thumb">
                    <img [src]="src" alt="During" />
                    <button type="button" class="ap-edit-thumb-remove"
                            (click)="removeEditPhoto('during', i)" aria-label="Remove">
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                }
                @if (editDuringPreviews().length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
              </div>
              <label class="ap-edit-photo-add">
                <i class="fa-solid fa-plus"></i> Add Photo
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple
                       class="sr-only" (change)="onEditPhotoSelected('during', $event)" />
              </label>
            </div>

            <!-- After Work -->
            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-after">
                <i class="fa-solid fa-circle-check"></i> After Work
              </div>
              <div class="ap-edit-photo-date">
                <label>After Date</label>
                <input type="date" [ngModel]="editFormData().photoDateAfter"
                       (ngModelChange)="patchEditForm({photoDateAfter: $event})" [ngModelOptions]="{standalone: true}">
              </div>
              <div class="ap-edit-photo-thumbs">
                @for (src of editAfterPreviews(); track src; let i = $index) {
                  <div class="ap-edit-thumb">
                    <img [src]="src" alt="After" />
                    <button type="button" class="ap-edit-thumb-remove"
                            (click)="removeEditPhoto('after', i)" aria-label="Remove">
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                }
                @if (editAfterPreviews().length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
              </div>
              <label class="ap-edit-photo-add">
                <i class="fa-solid fa-plus"></i> Add Photo
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple
                       class="sr-only" (change)="onEditPhotoSelected('after', $event)" />
              </label>
            </div>

          </div>

          <!-- Edit Remarks -->
          <div class="ap-edit-form-group" style="margin-top: 16px;">
            <label>Edit Remarks</label>
            <textarea rows="2"
                      [ngModel]="editFormData().editRemarks"
                      (ngModelChange)="patchEditForm({editRemarks: $event})"
                      placeholder="Why are you editing this project? (optional)"></textarea>
          </div>
        </div>

        <div class="ap-edit-modal-footer">
          <button class="ap-btn-secondary" (click)="closeEditModal()" [disabled]="isEditSubmitting() || isEditUploadingPhotos()">
            Cancel
          </button>
          <button class="ap-edit-btn-submit"
                  (click)="submitApproverEdit()"
                  [disabled]="isEditSubmitting() || isEditUploadingPhotos()"
                  [class.ap-edit-btn-approve]="pendingEditMode()">
            <i class="fa-solid"
               [class.fa-spinner]="isEditSubmitting() || isEditUploadingPhotos()"
               [class.fa-circle-check]="!isEditSubmitting() && !isEditUploadingPhotos() && pendingEditMode()"
               [class.fa-paper-plane]="!isEditSubmitting() && !isEditUploadingPhotos() && !pendingEditMode()"></i>
            <span>
              @if (isEditUploadingPhotos()) { Uploading… {{ editPhotoProgress() }}% }
              @else if (isEditSubmitting()) { Processing… }
              @else if (pendingEditMode()) { Approve with Changes }
              @else { Submit Edit for Approval }
            </span>
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ════════════════════════════════════════════════════════════════
       PENDING PROJECT VIEW MODAL
       ════════════════════════════════════════════════════════════════ -->
  @if (isPendingViewOpen()) {

    <!-- Lightbox for photos -->
    @if (pendingViewLightbox()) {
      <div class="ap-pv-lightbox" (click)="closePendingViewLightbox()" role="dialog" aria-modal="true">
        <button class="ap-pv-lb-close" (click)="closePendingViewLightbox()" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <img [src]="pendingViewLightbox()!" alt="Site photo" (click)="$event.stopPropagation()" />
      </div>
    }

    <div class="ap-pv-overlay" (click)="closePendingView()">
      <div class="ap-pv-panel" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">

        <!-- Header -->
        <div class="ap-pv-header">
          <div class="ap-pv-header-left">
            @if (pendingViewRequest()?.operation === 'create') {
              <span class="ap-pv-op-badge ap-pv-op-create"><i class="fa-solid fa-plus"></i> NEW PROJECT</span>
            } @else {
              <span class="ap-pv-op-badge ap-pv-op-edit"><i class="fa-solid fa-pen-line"></i> EDIT REQUEST</span>
            }
            <h2 class="ap-pv-title">
              {{ pendingViewData()?.['name'] || pendingViewData()?.['roadName'] || '—' }}
            </h2>
            <div class="ap-pv-chips">
              @if (pendingViewData()?.['block'] || pendingViewData()?.['loc']) {
                <span class="ap-pv-chip">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="ap-pv-chip-lbl">Block</span>
                  {{ pendingViewData()?.['block'] || pendingViewData()?.['loc'] }}
                </span>
              }
              @if (pendingViewData()?.['gp'] || pendingViewData()?.['gpName']) {
                <span class="ap-pv-chip">
                  <i class="fa-solid fa-landmark"></i>
                  <span class="ap-pv-chip-lbl">GP</span>
                  {{ pendingViewData()?.['gp'] || pendingViewData()?.['gpName'] }}
                </span>
              }
              @if (pendingViewData()?.['scheme']) {
                <span class="ap-pv-chip">
                  <i class="fa-solid fa-layer-group"></i>
                  <span class="ap-pv-chip-lbl">Scheme</span>
                  {{ pendingViewData()?.['scheme'] }}
                </span>
              }
              @if (pendingViewData()?.['dept']) {
                <span class="ap-pv-chip ap-pv-chip-dept">
                  <i class="fa-solid fa-building-columns"></i>
                  {{ pendingViewData()?.['dept'] }}
                </span>
              }
              @if (pendingViewData()?.['projectId']) {
                <span class="ap-pv-chip ap-pv-chip-id">
                  <i class="fa-solid fa-fingerprint"></i>
                  {{ pendingViewData()?.['projectId'] }}
                </span>
              }
            </div>
            <div class="ap-pv-meta">
              @if (pendingViewSubmittedDate()) {
                <span><i class="fa-solid fa-clock"></i> Submitted {{ pendingViewSubmittedDate() | date:'dd MMM yyyy' }}</span>
              }
              @if (pendingViewOperatorName() !== '—') {
                <span><i class="fa-solid fa-user"></i> By {{ pendingViewOperatorName() }}</span>
              }
            </div>
          </div>
          <button class="ap-stat-modal-close" (click)="closePendingView()" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Scrollable body -->
        <div class="ap-pv-body custom-scroll">

          <!-- 7 Detail Cards -->
          <div class="ap-pv-cards">
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-indian-rupee-sign"></i> Total Cost</span>
              <span class="ap-pv-card-val">
                @if (pendingViewData()?.['cost'] != null) { ₹{{ pendingViewData()?.['cost'] }} L }
                @else { — }
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-coins"></i> Amount Spent</span>
              <span class="ap-pv-card-val">
                @if (pendingViewData()?.['spent'] != null) { ₹{{ pendingViewData()?.['spent'] }} L }
                @else { — }
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-circle-half-stroke"></i> Status</span>
              <span [class]="projectStatusClass(pendingViewData()?.['status'])">
                {{ pendingViewData()?.['status'] || '—' }}
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-calendar-days"></i> Year of Creation</span>
              <span class="ap-pv-card-val">
                {{ pendingViewData()?.['yearOfCreation'] || pendingViewData()?.['sanctionedYear'] || '—' }}
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-road"></i> Road Length</span>
              <span class="ap-pv-card-val">
                @if (pendingViewData()?.['roadLength'] != null) { {{ pendingViewData()?.['roadLength'] }} km }
                @else { — }
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-user-tie"></i> Executant Name</span>
              <span class="ap-pv-card-val">
                {{ pendingViewData()?.['executorName'] || pendingViewData()?.['executant']?.['name'] || '—' }}
              </span>
            </div>
            <div class="ap-pv-card">
              <span class="ap-pv-card-lbl"><i class="fa-solid fa-phone"></i> Executant Contact</span>
              <span class="ap-pv-card-val">
                {{ pendingViewData()?.['executorNumber'] || pendingViewData()?.['executant']?.['contact'] || '—' }}
              </span>
            </div>
          </div>

          <!-- Photos Section -->
          <div class="ap-edit-section-title" style="margin-top: 4px;">
            <i class="fa-solid fa-images"></i> Site Photos
          </div>
          <div class="ap-edit-photos-grid">

            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-before">
                <i class="fa-solid fa-hourglass-start"></i> Before Work
              </div>
              @if (pendingViewDates().before) {
                <div class="ap-edit-photo-date-view"><i class="fa-regular fa-calendar"></i> {{ pendingViewDates().before | date:'mediumDate' }}</div>
              }
              <div class="ap-edit-photo-thumbs">
                @if (pendingViewPhotos().before.length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
                @for (src of pendingViewPhotos().before; track src) {
                  <div class="ap-edit-thumb ap-pv-thumb" (click)="openPendingViewLightbox(src)" title="Click to enlarge">
                    <img [src]="src" alt="Before" />
                  </div>
                }
              </div>
            </div>

            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-during">
                <i class="fa-solid fa-person-digging"></i> During Work
              </div>
              @if (pendingViewDates().during) {
                <div class="ap-edit-photo-date-view"><i class="fa-regular fa-calendar"></i> {{ pendingViewDates().during | date:'mediumDate' }}</div>
              }
              <div class="ap-edit-photo-thumbs">
                @if (pendingViewPhotos().during.length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
                @for (src of pendingViewPhotos().during; track src) {
                  <div class="ap-edit-thumb ap-pv-thumb" (click)="openPendingViewLightbox(src)" title="Click to enlarge">
                    <img [src]="src" alt="During" />
                  </div>
                }
              </div>
            </div>

            <div class="ap-edit-photo-box">
              <div class="ap-edit-photo-label ap-photo-after">
                <i class="fa-solid fa-circle-check"></i> After Work
              </div>
              @if (pendingViewDates().after) {
                <div class="ap-edit-photo-date-view"><i class="fa-regular fa-calendar"></i> {{ pendingViewDates().after | date:'mediumDate' }}</div>
              }
              <div class="ap-edit-photo-thumbs">
                @if (pendingViewPhotos().after.length === 0) {
                  <div class="ap-edit-photo-empty"><i class="fa-regular fa-image"></i> No photos</div>
                }
                @for (src of pendingViewPhotos().after; track src) {
                  <div class="ap-edit-thumb ap-pv-thumb" (click)="openPendingViewLightbox(src)" title="Click to enlarge">
                    <img [src]="src" alt="After" />
                  </div>
                }
              </div>
            </div>

          </div>

          <!-- Reject sub-dialog (inline) -->
          @if (isRejectDialogOpen()) {
            <div class="ap-pv-reject-dialog">
              <div class="ap-pv-reject-dialog-title">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>Reject this request</span>
              </div>
              <textarea class="ap-pv-reject-textarea"
                        rows="3"
                        [ngModel]="rejectDialogRemarks()"
                        (ngModelChange)="rejectDialogRemarks.set($event)"
                        placeholder="Add rejection remarks (optional)…"></textarea>
              <div class="ap-pv-reject-dialog-actions">
                <button class="ap-btn-secondary"
                        (click)="cancelRejectDialog()"
                        [disabled]="isSubmittingFromView()">
                  Cancel
                </button>
                <button class="ap-btn-reject"
                        (click)="confirmRejectFromView()"
                        [disabled]="isSubmittingFromView()">
                  @if (isSubmittingFromView()) { <i class="fa-solid fa-spinner fa-spin"></i> Rejecting… }
                  @else { <i class="fa-solid fa-circle-xmark"></i> Confirm Reject }
                </button>
              </div>
            </div>
          }

        </div>

        <!-- Footer action buttons -->
        <div class="ap-pv-footer">
          <button class="ap-btn-secondary ap-pv-edit-btn"
                  (click)="editFromPendingView()"
                  [disabled]="isSubmittingFromView()"
                  title="Edit data before approving">
            <i class="fa-solid fa-pen-to-square"></i>
            Edit Before Approval
          </button>
          <div class="ap-pv-footer-right">
            @if (!isRejectDialogOpen()) {
              <button class="ap-btn-reject"
                      (click)="openRejectDialog()"
                      [disabled]="isSubmittingFromView()">
                <i class="fa-solid fa-circle-xmark"></i>
                Reject
              </button>
            }
            <button class="ap-btn-approve"
                    (click)="approveFromView()"
                    [disabled]="isSubmittingFromView()">
              @if (isSubmittingFromView() && !isRejectDialogOpen()) {
                <i class="fa-solid fa-spinner fa-spin"></i> Approving…
              } @else {
                <i class="fa-solid fa-circle-check"></i> Approve Project
              }
            </button>
          </div>
        </div>

      </div>
    </div>
  }

  <!-- Compare Changes Modal (Approve / Reject inside) -->
  <app-compare-changes
    [isOpen]="isCompareOpen()"
    [beforeData]="beforeData()"
    [newData]="afterData()"
    [meta]="modalMeta()"
    [readOnly]="isReadOnly()"
    [isSubmitting]="isSubmittingAction()"
    (close)="isCompareOpen.set(false)"
    (approve)="approve(selectedRequest(), $any($event))"
    (reject)="reject(selectedRequest(), $any($event))"
  ></app-compare-changes>

  <!-- Toast notification (success / error feedback after every action) -->
  @if (toast(); as t) {
    <div class="ap-toast"
         [class.ap-toast--success]="t.kind === 'success'"
         [class.ap-toast--error]="t.kind === 'error'"
         role="alert" aria-live="polite">
      <div class="ap-toast-icon">
        @if (t.kind === 'success') { <i class="fa-solid fa-circle-check"></i> }
        @else { <i class="fa-solid fa-triangle-exclamation"></i> }
      </div>
      <div class="ap-toast-body">
        <p class="ap-toast-title">{{ t.title }}</p>
        @if (t.lines && t.lines.length) {
          <ul class="ap-toast-lines">
            @for (line of t.lines; track line) {
              <li>{{ line }}</li>
            }
          </ul>
        }
      </div>
      <button class="ap-toast-close" (click)="dismissToast()" aria-label="Dismiss">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
  }
</div>
